import {
  reactive,
  computed,
  provide,
  inject,
  onUnmounted,
} from 'vue';

const REGISTRY_KEY = 'errorRegistry';

/**
 * Registro de errores de validación reportados por componentes hijos.
 *
 * Reemplaza dos mecanismos que convivían para el mismo problema: flags escritos
 * directo en el modelo de datos del elemento (ej. ErrorMaximoMaquinaPliego, que
 * viajaba pegado a datos de negocio sin serlo) y un contador manual
 * (`reportarError` + `_contadorErrores` + un ref `_teniaErrorX` y un `onUnmounted`
 * repetidos en cada componente que reportaba un error). Acá la desuscripción al
 * desmontar la maneja el composable mismo — el componente que llama a
 * `useErrorRegistro` no necesita `onUnmounted` propio.
 *
 * Se llama UNA vez por documento (el `*_edit.vue` de cada módulo: Cotización,
 * Solicitud, Especificación Técnica, Orden de Producción).
 *
 * @returns {{ tieneErrores: import('vue').ComputedRef<boolean>, mensajes: import('vue').ComputedRef<string[]> }}
 */
export function provideErrorRegistry() {
  const errores = reactive(new Map());

  const tieneErrores = computed(() => errores.size > 0);
  // Dos capas distintas (elemento y proceso, ej. errorMaximoMaquinaPliego) pueden reportar el
  // mismo texto para la misma falla real — se deduplica acá, en la lectura, para no mostrarlo
  // repetido; el registro en sí conserva ambas entradas (cada una con su propia clave).
  const mensajes = computed(() => [...new Set(Array.from(errores.values()).filter(Boolean))]);

  provide(REGISTRY_KEY, errores);

  return { tieneErrores, mensajes };
}

/**
 * Reporta (o limpia) un error de validación en el registro del ancestro más cercano
 * que llamó a `provideErrorRegistry`. Cada llamada a `useErrorRegistro` obtiene una
 * clave propia (un `Symbol`), así que dos instancias del mismo componente — dos
 * procesos, dos materiales — no se pisan entre sí, y no hace falta pasarle un id a
 * mano para lograrlo.
 *
 * Si no hay registro proveído más arriba (por ejemplo, en una story de Storybook que
 * no lo necesita), `reportar` no hace nada — no hace falta acordarse de mockearlo.
 *
 * @param {string} nombre Sólo para debug/legibilidad — no participa en la clave.
 * @returns {(activo: boolean, mensaje?: string) => void}
 */
export function useErrorRegistro(nombre) {
  const errores = inject(REGISTRY_KEY, null);
  const key = Symbol(nombre);

  const reportar = (activo, mensaje = '') => {
    if (!errores) return;
    if (activo) {
      errores.set(key, mensaje);
    } else {
      errores.delete(key);
    }
  };

  onUnmounted(() => {
    errores?.delete(key);
  });

  return reportar;
}
